import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import axios from 'axios';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import useFetchCatalogs from 'hooks/useFetchCatalogs';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import debounce from 'lodash/debounce';
import { useUserContext } from 'providers/UserProvider';
import { canUserOrStaffEditEntity, Permissions } from 'services/Authorization';
import strings from 'strings';
import { FormSchema, PaymentMode } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';
import {
  DATA_SHEET_QUESTIONS_PATHS,
  DATA_SHEET_QUESTIONS_PATHS_REQUIRED
} from 'UI/pages/EditDataSheet/EditDataSheet.constants';
import {
  addFeePercent,
  canChangeFeeAgreementField,
  createDataSheetFormData,
  formatDataSheetSchema,
  formatDataSheetUiSchema,
  removeFeePercentFromSchema
} from 'UI/pages/EditDataSheet/EditDataSheet.utils';
import { getErrorMessage } from 'UI/utils';

import {
  getAssignmentSheetById,
  saveAssignmentSheet,
  saveAssignmentSheetDraft
} from '../AssignmentSheetDrawer.services';
import {
  addFeeAgreementValuesToFormData,
  convertDraftToWhiteSheetFormat,
  formatDataSheetDataForAPI,
  getDataSheetCount,
  resetSection1Values
} from '../AssignmentSheetDrawer.utils';

import useAssignmentSheetSchema from './useAssignmentSheetSchema';

const { uiMessages, title } = strings.candidates.editDataSheet;

const MILLISECONDS_TO_WAIT = 400;

const useAssignmentSheet = ({
  assignmentSheetId,
  companyId,
  whiteSheet,
  onSaveAssignmentSheet,
  onClose
}) => {
  const saveButtonRef = useRef(null);
  const draftAssignmentSheetId = useRef(null);
  const [user] = useUserContext();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const { nextSignal: nextSaveSignal } = useLatestRequestSignal({ abortOnUnmount: false });
  const finalSaveSignalRef = useRef(null);
  const [formData, setFormData] = useState(null);
  const formDataRef = useRef(null);
  const updatesCountRef = useRef(0);

  const fetchAssignmentSheetById = useCallback(
    () => getAssignmentSheetById(assignmentSheetId),
    [assignmentSheetId]
  );

  const {
    state: { results: assignmentSheetData, error: errorData, status: statusAssignmentSheetData }
  } = useFetchWithStatusV2({
    promise: !formData && assignmentSheetId && fetchAssignmentSheetById,
    initialStatus: !formData && assignmentSheetId ? UIStatus.Loading : UIStatus.Success
  });

  const { catalogs, uiState: catalogsStatus } = useFetchCatalogs({ companyId });

  const [schemaState] = useAssignmentSheetSchema();
  const isFetching =
    statusAssignmentSheetData === UIStatus.Loading ||
    catalogsStatus === UIStatus.Loading ||
    schemaState.isFetching;

  const wasThereAnError =
    statusAssignmentSheetData === UIStatus.Error ||
    catalogsStatus === UIStatus.Error ||
    schemaState.error;

  useEffect(() => {
    const processData = async () => {
      if (isFetching) return;
      if (!errorData && assignmentSheetData?.schema.key) {
        const resetData = resetSection1Values(assignmentSheetData.data);
        const finalData = addFeeAgreementValuesToFormData(resetData);
        formDataRef.current = finalData;
        setFormData(finalData);
        return;
      }

      if (whiteSheet && Object.keys(whiteSheet).length > 0) {
        const whiteSheetVersion = convertDraftToWhiteSheetFormat(whiteSheet);

        setFormData(prevFormData => {
          const dataSheetFormData = createDataSheetFormData(
            prevFormData,
            whiteSheetVersion,
            catalogs?.feeAgreements.data.length > 0
          );

          const finalData = addFeeAgreementValuesToFormData(dataSheetFormData, {
            feeAgreements: catalogs?.feeAgreements?.data
          });
          formDataRef.current = finalData;
          return finalData;
        });

        return;
      }

      setFormData(prevFormData => {
        const finalData = addFeeAgreementValuesToFormData(prevFormData, {
          feeAgreements: catalogs?.feeAgreements?.data
        });
        formDataRef.current = finalData;
        return finalData;
      });
    };

    processData();
  }, [assignmentSheetData, catalogs, whiteSheet, errorData, isFetching]);

  const finalUiSchema = useMemo(() => {
    if (!schemaState.data?.ui) return null;
    const canUserEdit = canUserOrStaffEditEntity(user, null);
    const dataSheet = {
      id: assignmentSheetData?.id
    };
    const canModifyFeeGuarantee = canChangeFeeAgreementField(
      canUserEdit,
      dataSheet,
      Permissions.FeeAgreements.ModifyGuarantee
    );

    const canModifyFeePercentage = canChangeFeeAgreementField(
      canUserEdit,
      dataSheet,
      Permissions.FeeAgreements.ModifyPercentage
    );

    const formattedUiSchema = formatDataSheetUiSchema(schemaState.data.ui, {
      canModifyFeeGuarantee,
      canModifyFeePercentage
    });

    return formattedUiSchema;
  }, [assignmentSheetData, schemaState, user]);

  const finalSchema = useMemo(() => {
    if (!schemaState.data?.structure) return null;
    if (!catalogs?.feeAgreements?.data) return schemaState.data.structure;
    const formattedSchema = formatDataSheetSchema(
      schemaState.data.structure,
      catalogs,
      whiteSheet ?? {}
    );

    const selectedFeeAgreement = catalogs.feeAgreements.data.find(
      feeAgreement => feeAgreement.id === formData?.section1?.feeAgreement
    );

    if (selectedFeeAgreement?.fee_agreement_payment_scheme_id === PaymentMode.Flat) {
      return removeFeePercentFromSchema(formattedSchema);
    }

    const schemaWithPercent = addFeePercent(formattedSchema);

    return schemaWithPercent;
  }, [schemaState, whiteSheet, catalogs, formData]);

  const onSaveClick = () => {
    if (!saveButtonRef.current) return;
    saveButtonRef.current.click();
  };

  const handleSaveDataSheetRef = useRef(null);
  const debouncedSaveDataSheet = useMemo(
    () => debounce((...args) => handleSaveDataSheetRef.current(...args), MILLISECONDS_TO_WAIT),
    []
  );

  const handleSaveDataSheet = useCallback(
    async (form, { shouldDisplayLoading = true, isDraft = false, shouldClose = true }) => {
      if (isDraft && finalSaveSignalRef.current) return;

      const finalData = addFeeAgreementValuesToFormData(form.formData, {
        feeAgreements: catalogs?.feeAgreements?.data
      });
      if (!isDraft) {
        debouncedSaveDataSheet.cancel();
        formDataRef.current = finalData;
        setFormData(finalData);
      }

      const hasFeeAgreementChanged =
        finalData.section1?.feeAgreement !== formDataRef.current?.section1?.feeAgreement;

      if (hasFeeAgreementChanged && updatesCountRef.current > 0) {
        const calculatedData = addFeeAgreementValuesToFormData(form.formData, {
          feeAgreements: catalogs?.feeAgreements?.data,
          hasFeeAgreementChanged
        });
        formDataRef.current = calculatedData;
        setFormData(calculatedData);
      }

      updatesCountRef.current += 1;
      if (isDraft && updatesCountRef.current === 1) {
        const sanitizedForm = formDataRef.current;
        const finalInfo = addFeeAgreementValuesToFormData(sanitizedForm, {
          feeAgreements: catalogs?.feeAgreements?.data,
          hasFeeAgreementChanged
        });
        setFormData(finalInfo);
        return;
      }

      const progress = getDataSheetCount({
        formData: isDraft ? finalData : formDataRef.current,
        questionsPaths: DATA_SHEET_QUESTIONS_PATHS,
        schema: finalSchema
      });

      const progressWithRequiredQuestions = getDataSheetCount({
        formData: isDraft ? finalData : formDataRef.current,
        questionsPaths: DATA_SHEET_QUESTIONS_PATHS_REQUIRED,
        schema: finalSchema
      });

      const finalAssignmentSheetId = draftAssignmentSheetId.current || assignmentSheetId;
      const data = formatDataSheetDataForAPI({
        id: finalAssignmentSheetId,
        formData: isDraft ? finalData : formDataRef.current,
        schemaName: FormSchema.JobOrderDataSheet,
        user,
        progress,
        schemaVersion: schemaState.data.version
      });

      const signal = nextSaveSignal();
      if (!isDraft) finalSaveSignalRef.current = signal;

      try {
        shouldDisplayLoading && setIsSaving(true);

        const response = await (isDraft ? saveAssignmentSheetDraft : saveAssignmentSheet)(data, {
          signal
        });

        if ([HTTPStatusCodes.Ok, HTTPStatusCodes.Created].includes(response.status)) {
          if (response.data.id) {
            draftAssignmentSheetId.current = response.data.id;
            onSaveAssignmentSheet(response.data.id, progressWithRequiredQuestions);
          }

          const finalMessage =
            progress.answered === progress.total
              ? uiMessages.complete.success
              : uiMessages.draft.success;

          shouldDisplayLoading &&
            dispatch(
              showAlert({
                severity: 'success',
                title: 'Success',
                body: finalMessage
              })
            );

          shouldDisplayLoading && setIsSaving(false);
          shouldClose && onClose();

          return;
        }

        const errorMessage =
          progress.answered === progress.total ? uiMessages.complete.error : uiMessages.draft.error;

        shouldDisplayLoading &&
          dispatch(
            showAlert({
              severity: 'error',
              title,
              body: errorMessage
            })
          );

        shouldDisplayLoading && setIsSaving(false);
      } catch (error) {
        if (shouldDisplayLoading && !axios.isCancel(error)) {
          dispatch(
            showAlert({
              severity: 'error',
              title,
              body: getErrorMessage(error)
            })
          );
        }

        shouldDisplayLoading && setIsSaving(false);
      } finally {
        if (finalSaveSignalRef.current === signal) finalSaveSignalRef.current = null;
      }
    },
    [
      assignmentSheetId,
      dispatch,
      schemaState,
      onSaveAssignmentSheet,
      user,
      onClose,
      finalSchema,
      catalogs,
      nextSaveSignal,
      debouncedSaveDataSheet
    ]
  );
  handleSaveDataSheetRef.current = handleSaveDataSheet;

  const flushPendingSave = useCallback(
    () => debouncedSaveDataSheet.flush(),
    [debouncedSaveDataSheet]
  );

  useEffect(() => {
    return () => flushPendingSave();
  }, [flushPendingSave]);

  const handleOnChange = useCallback(
    form => {
      const hasFeeAgreementChanged =
        form.formData.section1?.feeAgreement !== formDataRef.current?.section1?.feeAgreement;
      const nextFormData = addFeeAgreementValuesToFormData(form.formData, {
        feeAgreements: catalogs?.feeAgreements?.data,
        hasFeeAgreementChanged
      });
      formDataRef.current = nextFormData;
      setFormData(nextFormData);
      debouncedSaveDataSheet(
        { ...form, formData: nextFormData },
        {
          shouldDisplayLoading: false,
          isDraft: true,
          shouldClose: false
        }
      );
    },
    [catalogs, debouncedSaveDataSheet]
  );

  const finalSchemaState = useMemo(() => {
    if (!schemaState.data) return null;
    return {
      ...schemaState,
      data: { ...schemaState.data, ui: finalUiSchema, structure: finalSchema }
    };
  }, [schemaState, finalUiSchema, finalSchema]);

  return [
    {
      wasThereAnError,
      saveButtonRef,
      isSaving,
      formData,
      assignmentSheetData,
      isFetchingAssignmentSheetData: isFetching,
      errorAssignmentSheetData: errorData,
      primaryButtonName: assignmentSheetId ? strings.shared.ui.update : strings.shared.ui.save,
      ...finalSchemaState
    },
    {
      onSaveClick,
      save: handleSaveDataSheet,
      handleOnChange,
      flushPendingSave
    }
  ];
};

export default useAssignmentSheet;
